import { api } from './client';
import { i18nMetaMap } from '@/lib/i18n-map';
import '@/lib/i18n-map';

export interface AppNotification {
  id: string;
  type: string | null;
  text: string;
  link: string | null;
  read: boolean;
  createdAt: string;
}

/** One notice a rule produced — sent, failed, or skipped with the reason. */
export interface NoticeLogRow {
  id: string;
  event: string | null;
  channel: string | null;
  to: string | null;
  status: string | null;
  error: string | null;
  patientId: string | null;
  refId: string | null;
  sentAt: string;
}

export interface ReminderLogRow {
  id: string;
  appointmentId: string | null;
  channel: string | null;
  /** The number or address it went to. */
  to: string | null;
  status: string | null;
  providerSid: string | null;
  error: string | null;
  sentAt: string;
}

/**
 * What the type of a notification means to the reader.
 *
 * The server writes a free-text type; anything it has not been taught renders
 * with the neutral bell rather than disappearing, because a notification whose
 * kind this build does not know still has to be readable.
 */
export const NOTIFICATION_META = i18nMetaMap<string, { icon: string; label: string }>('common', {
  // Cheile trebuie să fie exact tipurile pe care le scrie serverul, altfel
  // notificarea apare cu clopoțelul neutru și fără etichetă. Serverul emite azi:
  // `stocare` (stoc scăzut), `laborator`, `portal`, `imagistica*`, `control`,
  // `securitate` (semnele unui acces neautorizat, `security-alerts`),
  // `termen` (documente care expiră și revizii scadente, `due-notices`) și
  // `cereri` (o cerere nouă de la un pacient nou, `inquiries`).
  stoc: { icon: '📦', label: 'stoc' },
  stocare: { icon: '📦', label: 'stoc' },
  laborator: { icon: '🦷', label: 'laboratorDentar' },
  programari: { icon: '🗓️', label: 'programari' },
  financiar: { icon: '💰', label: 'financiar' },
  personal: { icon: '👥', label: 'resurseUmane' },
  portal: { icon: '💬', label: 'portal' },
  imagistica: { icon: '🩻', label: 'imagistica' },
  imagistica_eroare: { icon: '⚠️', label: 'imagistica' },
  imagistica_semnat: { icon: '📄', label: 'imagistica' },
  imagistica_urgent: { icon: '⚠️', label: 'imagistica' },
  imagistica_link: { icon: '🔗', label: 'imagistica' },
  control: { icon: '🛡️', label: 'control' },
  securitate: { icon: '🔐', label: 'securitate' },
  termen: { icon: '⏰', label: 'termene' },
  // Cererile pacienților noi: formularul de pe site și botul de Telegram.
  cereri: { icon: '📥', label: 'cereriPacienti' },
});

/** Ce poate afla clinica despre SMS-urile ei. Nimic despre furnizor. */
export interface SmsStatus {
  /** Numele sub care primesc pacienții mesajele: „Ortodontica". */
  from: string | null;
  /** Aliasul platformei, „DentFlow" — cealaltă alegere pentru un test. */
  defaultFrom: string | null;
  /** Se poate trimite un SMS acum? */
  ready: boolean;
  /** Pleacă mesaje automate? Fals = regulile și reamintirile sunt oprite. */
  automated: boolean;
}

export interface SmsTestResult {
  status: 'sent' | 'failed' | 'skipped';
  to: string;
  /** Aliasul sub care a plecat. */
  from: string | null;
  text: string;
  /** Ce a răspuns gateway-ul la un eșec, cuvânt cu cuvânt. */
  error: string | null;
  reason: string | null;
}

export interface AbandonedNotice {
  event: string;
  refId: string;
  patientId: string | null;
  patientName: string | null;
  to: string | null;
  failures: number;
  lastAt: string;
  error: string | null;
  /** Adevărat: retrimis pe loc. Fals: pleacă la următoarea rulare a regulii, dacă mai e datorat. */
  immediate: boolean;
}

export interface MessagingUsageRow {
  /** YYYY-MM */
  month: string;
  channel: string;
  sent: number;
  failed: number;
}

export const notificationsApi = {
  list: (limit = 40) => api.get<AppNotification[]>('/notifications', { query: { limit } }),
  markRead: (id: string) => api.post<void>(`/notifications/${id}/read`),
  markAllRead: () => api.post<void>('/notifications/read-all'),
  /**
   * Hides it from the caller's own bell — no permission needed.
   *
   * This replaced the clinic-wide delete on the bell (NOT-9): a person clearing
   * their own bell should not remove the notification for everyone. The
   * `DELETE /notifications/:id` route stays on the server, behind
   * `notificari.trimite`, for a moderation screen that does not exist yet; it
   * has no client method until something calls it.
   */
  dismiss: (id: string) => api.post<void>(`/notifications/${id}/dismiss`),
  /** Newest first; `before` is the `sentAt` of the last row already shown. */
  noticesLog: (limit = 50, before?: string) =>
    api.get<NoticeLogRow[]>('/notifications/notices/log', { query: { limit, before } }),
  reminderLog: (limit = 50, before?: string) =>
    api.get<ReminderLogRow[]>('/notifications/reminders/log', { query: { limit, before } }),
  /** Mesajele la care regulile au renunțat după trei eșecuri. */
  abandoned: () => api.get<AbandonedNotice[]>('/notifications/notices/abandoned'),
  retryNotice: (body: { event: string; refId: string }) =>
    api.post<{ outcome: string }>('/notifications/notices/retry', body),
  /** Mesajele trimise pe lună și canal, ultimele 12 luni. */
  usage: () => api.get<{ months: MessagingUsageRow[] }>('/notifications/usage'),
  runReminders: () => api.post<{ sent: number; failed: number }>('/notifications/reminders/run'),

  smsStatus: () => api.get<SmsStatus>('/notifications/sms/status'),
  /**
   * Un SMS, trimis dinadins, către un număr scris de om.
   *
   * Pleacă și cât timp mesajele automate sunt oprite — e trimis de un om. Un
   * rezultat `skipped` cu motiv (clinică demonstrativă, număr care nu e mobil)
   * e un răspuns, nu o eroare.
   */
  sendTestSms: (to: string, text?: string, sender: 'clinic' | 'platform' = 'clinic') =>
    api.post<SmsTestResult>('/notifications/sms/test', {
      to,
      text: text?.trim() || undefined,
      sender,
    }),

  /** Null until the platform has a VAPID key pair; the bell hides the switch. */
  pushKey: () => api.get<{ publicKey: string | null }>('/notifications/push/key'),
  pushSubscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api.post<{ ok: boolean }>('/notifications/push/subscribe', body),
  pushUnsubscribe: (endpoint: string) =>
    api.post<{ ok: boolean }>('/notifications/push/unsubscribe', { endpoint }),
  pushTest: () =>
    api.post<{ subscriptions: number; sent: number; failed: number }>('/notifications/push/test'),
};
